import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

export const Contact = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    alert('Thank you for contacting us! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${require("../images/farm3.jpeg")})` }}
        />
        <div className="absolute inset-0 bg-farm-dark/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6"
        >
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-farm-cream mb-6">
            Get In <span className="text-farm-gold">Touch</span>
          </h1>
          <p className="text-xl text-farm-cream/80 max-w-2xl mx-auto">
            We'd love to hear from you
          </p>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section ref={containerRef} className="py-32 bg-farm-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center text-center max-w-4xl w-full"
            >
              <h2 className="text-5xl font-serif font-bold text-farm-cream mb-6">
                Contact Information
              </h2>
              <p className="text-farm-cream/70 mb-16 text-lg max-w-xl">
                Reach out to us for inquiries about our dairy products, horse breeding services, or farm visits.
              </p>

              <div className="grid md:grid-cols-2 gap-8 w-full">
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center text-center gap-4 p-8 bg-farm-brown/10 border border-farm-green/20 rounded-3xl"
                >
                  <div className="w-12 h-12 bg-farm-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-farm-dark" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-farm-cream mb-2">Address</h3>
                    <p className="text-farm-cream/70">
                      Chak No# 134/9-L, Sahiwal<br />
                      Punjab, Pakistan
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center text-center gap-4 p-8 bg-farm-brown/10 border border-farm-green/20 rounded-3xl"
                >
                  <div className="w-12 h-12 bg-farm-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-farm-dark" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-farm-cream mb-2">Phone</h3>
                    <p className="text-farm-cream/70">+92 300 8698834 <br /> +92 321 8698134</p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center text-center gap-4 p-8 bg-farm-brown/10 border border-farm-green/20 rounded-3xl"
                >
                  <div className="w-12 h-12 bg-farm-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-farm-dark" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-farm-cream mb-2">Email</h3>
                    <p className="text-farm-cream/70">sahiwalfarmss@gmail.com</p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center text-center gap-4 p-8 bg-farm-brown/10 border border-farm-green/20 rounded-3xl"
                >
                  <div className="w-12 h-12 bg-farm-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-farm-dark" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-farm-cream mb-2">Working Hours</h3>
                    <p className="text-farm-cream/70">
                      Monday - Saturday: 8:00 AM - 6:00 PM<br />
                      Sunday: 9:00 AM - 2:00 PM
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div >
      </section >

      {/* Map Section */}
      < section className="h-96 w-full relative overflow-hidden" >
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3433.2757329427423!2d73.0745119!3d30.6261858!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3922c9084b0b3d7b%3A0x9a99e7cf30e3c057!2sSahiwal%20Farms%20Ltd!5e0!3m2!1sen!2spk!4v1700000000000!5m2!1sen!2spk"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Sahiwal Farms Ltd Location Map"
          className="w-full h-full grayscale contrast-125 opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
        />
      </section >
    </div >
  );
};